import { Button, Icon } from '@iwoca/orion';
import { fetchPostTruelayerAuthorizationUrl } from '@iwoca/lapi-client/edge';
import { useStateKey } from '../../../../../../../iwQueryClient/useStateKey';
import { useMutation } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from './TrueLayerLink.messages';
import TrueLayerLinkedAccounts from '../../../../../../../myAccount/modals/linkOpenBankingModal/trueLayer/TrueLayerLinkedAccounts';
import { useEffect, useState } from 'react';
import { getQueryParameter, getWindowLocation } from '@iwRouter/LocationHelper';
import {
    TRUELAYER_CANCEL,
    TRUELAYER_FAILURE,
    TRUELAYER_SUCCESS
} from '../../../../../../../myAccount/modals/linkOpenBankingModal/trueLayer/truelayer.constants';
import { TruelayerAlertBox } from '../../../../../../../myAccount/modals/linkOpenBankingModal/trueLayer/TruelayerAlertbox';
import { tracking } from '@iwoca/frontend-tracking-library';
import { ListItem, Text, UnorderedList } from '@ui';

const TrueLayerLink = ({
    toggleManualUpload
}: {
    toggleManualUpload: () => void;
}) => {
    const stateKey = useStateKey();
    const intl = useIntl();

    const [hasAuthorisationUrlErrored, setHasAuthorisationUrlError] =
        useState(false);

    const { isLoading, mutate: fetchTruelayerUrl } = useMutation({
        mutationFn: () =>
            fetchPostTruelayerAuthorizationUrl({ stateKey: stateKey! }),
        onSuccess: (data) => {
            if (data?.data?.authorization_url) {
                window.location.href = data.data.authorization_url;
            }
        },
        onError: () => {
            setHasAuthorisationUrlError(true);
        }
    });

    useEffect(() => {
        const statusCode = getQueryParameter(window.location, 'status_code');

        if (statusCode === '200') {
            tracking.ampli.newDocUploadsSuccessfullyLinkedBankAccountViaTruelayer();
        }
    }, []);

    const alertStatus = (() => {
        if (hasAuthorisationUrlErrored) {
            return TRUELAYER_FAILURE;
        }

        const location = getWindowLocation();
        const statusCode = getQueryParameter(location, 'status_code');
        const errorDetail = getQueryParameter(location, 'error_detail');
        if (statusCode) {
            if (statusCode === '200') {
                return TRUELAYER_SUCCESS;
            }

            if (
                statusCode === '500' &&
                errorDetail &&
                errorDetail === 'access_denied'
            ) {
                return TRUELAYER_CANCEL;
            }

            if (statusCode === '500' && errorDetail !== 'access_denied') {
                return TRUELAYER_FAILURE;
            }

            return TRUELAYER_FAILURE;
        }

        return null;
    })();

    const handleClick = () => {
        fetchTruelayerUrl();
    };

    return (
        <div className="mt-m space-y-l">
            <div className="flex flex-col gap-l sm:flex-row">
                <div className="space-y-l">
                    <Text className="text-structure-40">
                        Open Banking is a secure way to share your business
                        banking data without sharing your password. When you
                        link your account with Open Banking, it means we can
                        assess your application more quickly, now and in the
                        future.{' '}
                    </Text>
                    <UnorderedList>
                        <ListItem>
                            <FormattedMessage
                                {...messages.info}
                                values={{
                                    FCALink: (
                                        <a
                                            href={intl.formatMessage(
                                                messages.FCALink
                                            )}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="link"
                                        >
                                            <FormattedMessage
                                                {...messages.FCALinkText}
                                            />
                                        </a>
                                    ),
                                    link: (
                                        <a
                                            href={intl.formatMessage(
                                                messages.infoLink
                                            )}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="link"
                                        >
                                            <FormattedMessage
                                                {...messages.infoLinkText}
                                            />
                                        </a>
                                    )
                                }}
                            />
                        </ListItem>
                        <ListItem>
                            <FormattedMessage {...messages.infoTwo} />
                        </ListItem>
                    </UnorderedList>{' '}
                </div>

                <Button
                    onClick={handleClick}
                    variant="secondary"
                    loading={isLoading}
                    type="button"
                >
                    <FormattedMessage {...messages.linkCTA} />
                    <Icon icon="link" />
                </Button>
            </div>

            <TrueLayerLinkedAccounts />

            {alertStatus && (
                <TruelayerAlertBox
                    handleFailureButtonClick={toggleManualUpload}
                    handleCancelButtonClick={toggleManualUpload}
                    status={alertStatus}
                />
            )}

            <div className="flex items-start">
                <Icon
                    icon="fileWithItemsOutline"
                    className="mr-s mt-xs shrink-0 fill-structure-40"
                />
                <div className="flex flex-wrap">
                    <Text className="mr-xs text-structure-40">
                        Don't want to link your bank account?
                    </Text>{' '}
                    <button
                        onClick={() => toggleManualUpload()}
                        className="border-none bg-transparent px-0 font-med text-primary-40 underline hover:cursor-pointer"
                    >
                        Upload bank statements
                    </button>
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default TrueLayerLink;
